import { MouseEvent as ReactMouseEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useEventListener } from 'usehooks-ts';
import FocusTrap from 'focus-trap-react';

import { Button } from '../../button/button.component';
import { Keycodes } from '../../../utils/keyboard';
import { useOnClickOutside } from '../../../utils/use-on-click-outside';
import { useScrollBlock } from '../../../utils/use-scroll-block';

import WistiaVideo from '../wistia-video.component';
import { WistiaModalProps } from './wistia-modal.config';

import './wistia-modal.scss';

const WistiaModal = ({
  onClose,
  open,
  captionDescription,
  captionHeadline,
  showVideoLength,
  ...props
}: WistiaModalProps) => {
  const [blockScroll, allowScroll] = useScrollBlock();
  const dialogRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dialogRef, onClose);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === Keycodes.Escape) {
      onClose();
    }
  };

  const onButtonClose = (e?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    e?.stopPropagation();
    onClose();
  };

  useEventListener('keydown', onKeyDown);

  useEffect(() => {
    if (open) {
      blockScroll();
    } else {
      allowScroll();
    }
    return () => {
      allowScroll();
    };
  }, [open, allowScroll, blockScroll]);

  const modal = (
    <div data-testid="snowflake-wistia-modal" className="snowflake-wistia-modal">
      <FocusTrap focusTrapOptions={{ fallbackFocus: 'body' }}>
        <div ref={dialogRef} className="snowflake-wistia-modal-dialog" tabIndex={0} role="dialog">
          <WistiaVideo hasAutoplay {...props} />
          <div
            data-testid="snowflake-wistia-modal-dialog-close"
            className="snowflake-wistia-modal-dialog-close snowflake-button-white snowflake-button-icon"
          >
            <Button
              accessibilityLabel="Video modal close"
              onClick={onButtonClose}
              appliedCssClassNames="snowflake-button-icon"
              icon="xMark"
              disableAnchor
            />
          </div>
        </div>
      </FocusTrap>
    </div>
  );

  return open ? createPortal(modal, document.body) : null;
};

export default WistiaModal;
